import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  BrainCircuit,
  ChevronRight,
  FilePlus2,
  FileText,
  FolderOpen,
  List,
  LockKeyhole,
  Network,
  Pause,
  Play,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useLiveResource } from "@/hooks/useLiveResource";
import BrainSourceDialog, {
  sourceDate,
} from "@/components/brain/BrainSourceDialog";
import { buildCollections, scopedData } from "@/components/brain/solarModel";
import {
  ancestry,
  buildAtlas,
  neighborhood,
  ROOT,
} from "@/components/brain/atlasModel";
import { useChat } from "@/components/chat/ChatWorkspaceContext";
import AtlasMap from "@/components/brain/AtlasMap";
import PlaybookReader from "@/components/brain/PlaybookReader";
import "@/components/brain/solar.css";
import "@/components/brain/atlas.css";
const count = (value) => Number(value || 0).toLocaleString();
function BrainBody({ clientSlug }) {
  const { api, activeClient } = useApp(),
    [params] = useSearchParams();
  const agent = useLiveResource("getAgentBrain", [], { clearOnError: true }),
    brand = useLiveResource("getBrain", ["brand"], { clearOnError: true }),
    shared = useLiveResource("getBrain", ["shared"], { clearOnError: true }),
    files = useLiveResource("getAgentFiles", [], { clearOnError: true }),
    uploads = useLiveResource("getKnowledgeUploads", [], {
      clearOnError: true,
    }),
    catalog = useLiveResource("getBrainAtlas", [], { clearOnError: true });
  const agentData = scopedData(agent, clientSlug),
    catalogData = scopedData(catalog, clientSlug)?.accessGranted
      ? scopedData(catalog, clientSlug)
      : null,
    runtime = agentData?.runtime;
  const brandData = scopedData(brand, clientSlug, "brand"),
    sharedData = scopedData(shared, clientSlug, "shared"),
    fileData = scopedData(files, clientSlug),
    uploadData = scopedData(uploads, clientSlug);
  const collections = useMemo(
    () =>
      buildCollections({
        runtime: agentData?.runtime,
        brand: brandData,
        shared: sharedData,
        files: fileData,
        uploads: uploadData,
        connection: agentData?.smartMarketer,
      }),
    [agentData, brandData, sharedData, fileData, uploadData],
  );
  const chat = useChat();
  const agentName =
    chat?.name ||
    agentData?.agentName ||
    activeClient?.displayName ||
    "Your agent";
  const workspaceName =
    activeClient?.clientName || activeClient?.displayName || "your brand";
  const [focusId, setFocusId] = useState(
    params.get("collection") === "files" || params.get("view") === "brand"
      ? "brand"
      : params.get("view") === "shared"
        ? "shared"
        : ROOT,
  );
  const [opened, setOpened] = useState(null),
    [readerTab, setReaderTab] = useState("method"),
    [extraSources, setExtraSources] = useState([]),
    [located, setLocated] = useState(null),
    [query, setQuery] = useState(""),
    [search, setSearch] = useState(""),
    [moving, setMoving] = useState(true),
    [outline, setOutline] = useState(false),
    [page, setPage] = useState(0),
    [browse, setBrowse] = useState({
      status: "idle",
      sources: [],
      key: "",
      hasMore: false,
    }),
    [browseRetry, setBrowseRetry] = useState(0);
  const backStack = useRef([]),
    deepLink = useRef(null);
  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 240);
    return () => clearTimeout(t);
  }, [query]);
  const focusedSource = focusId.startsWith("catalog:")
    ? [...browse.sources, ...extraSources].find(
        (s) => `catalog:${s.id}` === focusId,
      )
    : null;
  const domain = focusId.startsWith("domain:")
    ? focusId.slice(7)
    : focusedSource?.domainId || null;
  const browseKey = `${domain || ""}|${search}`;
  useEffect(() => {
    setPage(0);
  }, [browseKey]);
  useEffect(() => {
    let active = true;
    if ((!domain && !search) || !catalogData?.accessGranted) {
      setBrowse({
        status: "idle",
        sources: [],
        key: browseKey,
        hasMore: false,
      });
      return;
    }
    setBrowse((p) => ({
      status: "loading",
      sources: p.key === browseKey ? p.sources : [],
      key: browseKey,
      hasMore: false,
    }));
    api
      .getBrainAtlas({
        domain: domain || undefined,
        q: search || undefined,
        offset: page,
      })
      .then((data) => {
        if (!active) return;
        if (data.clientSlug !== clientSlug || !data.accessGranted)
          throw new Error("scope");
        setBrowse((p) => ({
          status: "ready",
          key: browseKey,
          sources: [
            ...new Map(
              [
                ...(page && p.key === browseKey ? p.sources : []),
                ...data.sources,
              ].map((s) => [s.id, s]),
            ).values(),
          ],
          hasMore: data.hasMore,
          nextOffset: data.nextOffset,
        }));
      })
      .catch(() => {
        if (active)
          setBrowse({
            status: "error",
            key: browseKey,
            sources: [],
            hasMore: false,
          });
      });
    return () => {
      active = false;
    };
  }, [
    api,
    clientSlug,
    domain,
    search,
    page,
    catalogData?.generatedAt,
    catalogData?.accessGranted,
    browseKey,
    browseRetry,
  ]);
  useEffect(() => {
    setExtraSources([]);
  }, [catalogData?.generatedAt, catalogData?.accessGranted]);
  const catalogSources = useMemo(
    () =>
      catalogData?.accessGranted
        ? [
            ...new Map(
              [
                ...(browse.key === browseKey ? browse.sources : []),
                ...extraSources,
              ].map((s) => [s.id, s]),
            ).values(),
          ]
        : [],
    [
      catalogData?.accessGranted,
      extraSources,
      browse.key,
      browseKey,
      browse.sources,
    ],
  );
  const graph = useMemo(
    () => buildAtlas(collections, catalogData, agentName, catalogSources),
    [collections, catalogData, agentName, catalogSources],
  );
  const allEntries = graph.sections.flatMap((c) => c.entries),
    current = graph.nodes.find((n) => n.id === focusId) || graph.nodes[0];
  const path = ancestry(graph.nodes, current.id),
    { children } = neighborhood(graph.nodes, current.id);
  const source = allEntries.find((e) => e.id === opened),
    sourceCollection = graph.sections.find(
      (c) => c.id === source?.collectionId,
    );
  const playbook = catalogData?.playbooks?.find(
    (p) => `playbook:${p.id}` === opened,
  );
  const catalogSource = catalogSources.find(
    (s) => `catalog:${s.id}` === opened,
  );
  const stale = runtime && Date.now() - Date.parse(runtime.receivedAt) > 300000;
  const recent = allEntries
    .filter((e) => ["memory", "preference", "learning"].includes(e.kind))
    .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
    .slice(0, 3);
  const localResults = query.trim()
    ? graph.nodes.filter(
        (n) =>
          n.id !== ROOT &&
          `${n.title} ${n.summary || ""}`
            .toLowerCase()
            .includes(query.toLowerCase()),
      )
    : [];
  const packages = catalogData?.packages || [],
    sourcesTotal = packages.reduce((n, p) => n + p.sourceCount, 0),
    passagesTotal = packages.reduce((n, p) => n + p.passageCount, 0);
  const focus = (id) => {
    if (id !== focusId) backStack.current.push(focusId);
    setFocusId(id);
    setOpened(null);
    setLocated(null);
  };
  const back = () => {
    setOpened(null);
    setLocated(null);
    setFocusId(backStack.current.pop() || current.parent || ROOT);
  };
  const locate = (id) => {
    let n = graph.nodes.find((n) => n.id === id || n.recordId === id);
    if (!n) {
      const old = allEntries.find((e) => e.id === id || e.recordId === id);
      n = graph.nodes.find(
        (n) =>
          n.kind === "playbook" &&
          n.title.toLowerCase() === old?.title.toLowerCase(),
      );
    }
    if (!n) return;
    if (n.kind === "catalog")
      setExtraSources((p) => [
        ...p.filter((s) => s.id !== n.recordId),
        { ...n, id: n.recordId },
      ]);
    focus(n.parent || n.id);
    setLocated(n.id);
    setQuery("");
  };
  useEffect(() => {
    const id = params.get("entry");
    if (
      id &&
      deepLink.current !== id &&
      allEntries.some((e) => e.recordId === id)
    ) {
      deepLink.current = id;
      locate(id);
    }
  }, [params, allEntries]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (opened && !source && !playbook && !catalogSource) setOpened(null);
  }, [opened, source, playbook, catalogSource]);
  const refresh = () =>
    Promise.allSettled([
      agent.reload(),
      brand.reload(),
      shared.reload(),
      files.reload(),
      uploads.reload(),
      catalog.reload(),
    ]);
  const readNode = (n) => {
    if (n.kind === "catalog")
      setExtraSources((p) => [
        ...p.filter((s) => s.id !== n.recordId),
        { ...n, id: n.recordId },
      ]);
    setReaderTab(n.section || "method");
    if (n.kind === "playbookSection") setOpened(`playbook:${n.bookId}`);
    else if (
      n.nodeType === "entry" ||
      n.kind === "playbook" ||
      n.kind === "catalog"
    )
      setOpened(n.id);
  };
  const explorePlaybook = (p) => {
    setReaderTab("method");
    focus(`playbook:${p.id}`);
    setOpened(`playbook:${p.id}`);
  };
  return (
    <div className="solar-brain atlas-page" data-testid="brain-page">
      <header className="solar-page-header">
        <div>
          <div className="atlas-page-kicker">
            <BrainCircuit size={14} /> YOUR AGENT / KNOWLEDGE SYSTEM
          </div>
          <h1>
            The Brain<span>.</span>
          </h1>
          <p>
            Explore the expertise behind your agent. See how it connects to your
            brand.
          </p>
        </div>
        <Link to="/agent/add-files" className="solar-primary">
          <FilePlus2 size={16} />
          Add files
        </Link>
      </header>
      <section
        className="atlas-shell"
        aria-label="Agent knowledge constellation"
      >
        <div className="atlas-toolbar">
          <div className="atlas-breadcrumb">
            <button onClick={() => focus(ROOT)} aria-label="Show entire brain">
              <Network size={16} />
              <span>Entire brain</span>
            </button>
            {path.slice(1).map((n) => (
              <span key={n.id}>
                <ChevronRight size={13} />
                <button onClick={() => focus(n.id)}>{n.title}</button>
              </span>
            ))}
          </div>
          <div className="atlas-toolbar-actions">
            <button
              aria-label={moving ? "Pause map motion" : "Enable map motion"}
              onClick={() => setMoving((v) => !v)}
            >
              {moving ? <Pause size={15} /> : <Play size={15} />}
            </button>
            <button aria-label="Refresh brain" onClick={refresh}>
              <RefreshCw size={15} />
            </button>
            <button
              aria-label="Toggle accessible outline"
              aria-pressed={outline}
              onClick={() => setOutline((v) => !v)}
            >
              <List size={16} />
            </button>
          </div>
        </div>
        <div className="atlas-heading">
          <div>
            <span className="atlas-eyebrow">
              {current.id === ROOT
                ? "THE BIG PICTURE"
                : current.id === "shared"
                  ? "CONNECTED EXPERTISE"
                  : current.kind === "domain"
                    ? "INSIDE THE SOURCE LIBRARY"
                    : current.kind === "playbook"
                      ? "A METHOD IN YOUR SYSTEM"
                      : current.scope || "YOUR CONNECTED KNOWLEDGE"}
            </span>
            <h2>
              {current.id === ROOT
                ? "A whole world of know-how."
                : current.title}
            </h2>
            <p>
              {current.id === ROOT
                ? "Shared expertise. Your brand’s context. One connected view."
                : current.summary ||
                  current.description ||
                  "Explore the sources and connections around this topic."}
            </p>
          </div>
          <div className="atlas-status">
            <i className={runtime && !stale ? "healthy" : ""} />
            <span>
              {runtime
                ? stale
                  ? "Snapshot needs a refresh"
                  : "Connected to your agent"
                : "Runtime sync unavailable"}
              <small>
                {runtime
                  ? `Last read ${sourceDate(runtime.observedAt)}`
                  : "Refresh to check the connection"}
              </small>
            </span>
          </div>
        </div>
        <div className="atlas-search-row">
          <div className="atlas-search">
            <Search size={16} />
            <input
              aria-label="Search your agent’s brain"
              placeholder="Find a method, memory, skill or source…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                aria-label="Clear brain search"
                onClick={() => setQuery("")}
              >
                <X size={15} />
              </button>
            )}
            {query && (
              <div className="solar-search-results atlas-results">
                {localResults.map((n) => (
                  <button key={n.id} onClick={() => locate(n.id)}>
                    <span>
                      <strong>{n.title}</strong>
                      <small>{n.sourceLabel || n.kind}</small>
                    </span>
                    <ArrowRight size={15} />
                  </button>
                ))}
                {!localResults.length && (
                  <p>
                    {browse.status === "loading"
                      ? "Searching connected knowledge…"
                      : "No matching material found."}
                  </p>
                )}
                {browse.hasMore && (
                  <button onClick={() => setPage(browse.nextOffset)}>
                    Load more matching sources
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="atlas-scope-key">
            <span>
              <i />
              Private workspace
            </span>
            <span>
              <i />
              Shared expertise
            </span>
          </div>
        </div>
        <AtlasMap
          nodes={graph.nodes}
          edges={graph.edges}
          focusId={current.id}
          onFocus={focus}
          onOpen={readNode}
          located={located}
          moving={moving}
        />
        <nav
          className="atlas-mobile-neighborhood"
          aria-label="Browse this neighborhood"
        >
          {children.map((n) => (
            <button
              key={n.id}
              onClick={() => {
                focus(n.id);
                readNode(n);
              }}
            >
              {n.title}
              <ChevronRight size={13} />
            </button>
          ))}
        </nav>
        <div className="atlas-map-bottom">
          <button onClick={back} disabled={current.id === ROOT}>
            <ArrowLeft size={15} />
            Back one level
          </button>
          <span>
            {allEntries.length} saved records · {graph.sections.length}{" "}
            collections
          </span>
          <button onClick={() => focus("brand")}>
            <FolderOpen size={14} />
            Files & downloads
          </button>
        </div>
        {outline && (
          <div className="atlas-outline" aria-label="Knowledge outline">
            {children.length ? (
              children.map((n) => (
                <button
                  key={n.id}
                  onClick={() => {
                    focus(n.id);
                    readNode(n);
                  }}
                >
                  <span>{n.title}</span>
                  <small>{n.kind}</small>
                  <ChevronRight size={14} />
                </button>
              ))
            ) : (
              <p>
                Select a parent from the breadcrumb to explore its contents.
              </p>
            )}
          </div>
        )}
      </section>
      <div className="atlas-foundation-strip">
        <div className="atlas-foundation-brand">
          <span>
            <BookOpen size={22} />
          </span>
          <div>
            <strong>
              {agentData?.smartMarketer?.accessGranted
                ? "Connected to the Smart Marketer Brain"
                : "Smart Marketer knowledge"}
            </strong>
            <small>
              {packages.length
                ? packages
                    .map((p) => `Connected package v${p.version}`)
                    .join(" · ")
                : agentData?.smartMarketer?.packages
                    ?.map((p) => `v${p.version}`)
                    .join(" · ") || "No active package reported"}
            </small>
          </div>
        </div>
        <div>
          <strong>{count(sourcesTotal)}</strong>
          <span>source documents</span>
        </div>
        <div>
          <strong>{count(passagesTotal)}</strong>
          <span>indexed passages</span>
        </div>
        <div>
          <strong>{catalogData?.playbooks?.length || 0}</strong>
          <span>in-depth playbooks</span>
        </div>
        <button
          onClick={() => focus("shared")}
          aria-label="Explore shared knowledge"
        >
          Explore
          <ArrowRight size={15} />
        </button>
      </div>
      {catalog.status === "error" && (
        <div className="atlas-notice" role="status">
          The shared catalog could not be loaded. Your available agent records
          are shown above.
          <button onClick={() => catalog.reload().catch(() => {})}>
            Retry catalog
          </button>
        </div>
      )}
      {domain && (
        <section className="atlas-library">
          <div className="atlas-section-heading">
            <div>
              <span className="atlas-eyebrow">SOURCE LIBRARY</span>
              <h2>
                Inside{" "}
                {(
                  graph.nodes.find((n) => n.id === `domain:${domain}`)?.title ||
                  current.title
                ).toLowerCase()}
              </h2>
              <p>
                Original lesson metadata from the connected package. Select a
                source to locate it in the web.
              </p>
            </div>
            <span>
              {count(
                graph.nodes.find((n) => n.id === `domain:${domain}`)?.count,
              )}{" "}
              documents in this domain
            </span>
          </div>
          <div className="atlas-library-list">
            {catalogSources
              .filter((s) => s.domainId === domain)
              .map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setLocated(`catalog:${s.id}`);
                    setOpened(`catalog:${s.id}`);
                  }}
                >
                  <FileText size={20} />
                  <span>
                    <strong>{s.title}</strong>
                    <small>{s.course}</small>
                  </span>
                  <span>{s.passageCount} passages</span>
                  <ArrowRight size={15} />
                </button>
              ))}
          </div>
          {browse.status === "loading" && (
            <p role="status">Loading source library…</p>
          )}
          {browse.status === "error" && (
            <div role="alert">
              Could not load these sources.{" "}
              <button onClick={() => setBrowseRetry((x) => x + 1)}>
                Try again
              </button>
            </div>
          )}
          {browse.hasMore && (
            <button
              className="atlas-load-more"
              onClick={() => setPage(browse.nextOffset)}
            >
              Show more sources
              <ArrowRight size={15} />
            </button>
          )}
        </section>
      )}
      {!!catalogData?.playbooks?.length && (
        <section className="atlas-playbooks">
          <div className="atlas-section-heading">
            <div>
              <span className="atlas-eyebrow">GO FROM KNOWING TO DOING</span>
              <h2>Ideas worth putting to work.</h2>
              <p>
                Substantial methods. Real examples. A starting point for your
                brand.
              </p>
            </div>
            <span>OFFERS & FUNNELS · 01—03</span>
          </div>
          <div className="atlas-playbook-grid">
            {catalogData.playbooks.map((p, i) => (
              <button
                key={p.id}
                className={`atlas-playbook-tile atlas-tile-${i}`}
                onClick={() => explorePlaybook(p)}
              >
                <div className="atlas-tile-top">
                  <span>SMART MARKETER</span>
                  <span>0{i + 1}</span>
                </div>
                <div className={`atlas-method-art art-${i}`} aria-hidden="true">
                  <i />
                  <i />
                  <i />
                  <b />
                  <b />
                </div>
                <div className="atlas-tile-copy">
                  <small>PLAYBOOK / OFFERS & FUNNELS</small>
                  <h3>{p.title}</h3>
                  <p>{p.summary}</p>
                  <span>
                    Explore the method
                    <ArrowRight size={18} />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
      {recent.length > 0 && (
        <section className="atlas-recent">
          <div className="atlas-section-heading">
            <div>
              <span className="atlas-eyebrow">
                YOUR BRAND, IN ITS OWN WORDS
              </span>
              <h2>Saved by your agent.</h2>
              <p>
                Real workspace notes, with their original context one click
                away.
              </p>
            </div>
            <button onClick={() => focus("memory")}>
              Explore memory
              <ArrowRight size={15} />
            </button>
          </div>
          <div className="atlas-recent-list">
            {recent.map((e, i) => (
              <button
                className="solar-recent-card atlas-memory-row"
                key={e.id}
                onClick={() => locate(e.id)}
              >
                <span className="atlas-memory-number">0{i + 1}</span>
                <span className="atlas-memory-copy">
                  <small>{e.sourceLabel} · Private workspace</small>
                  <strong>{e.title}</strong>
                  <p>{e.summary}</p>
                </span>
                <span className="atlas-memory-end">
                  Locate in the web
                  <ArrowRight size={16} />
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
      <footer className="atlas-page-footer">
        <LockKeyhole size={12} />
        Private brand context. Connected shared expertise.
      </footer>
      {source && sourceCollection && (
        <BrainSourceDialog
          key={`${clientSlug}:${source.id}`}
          entry={source}
          collection={sourceCollection}
          clientSlug={clientSlug}
          agentName={agentName}
          workspaceName={workspaceName}
          entries={allEntries}
          observedAt={runtime?.observedAt}
          onClose={() => setOpened(null)}
          onLocate={locate}
        />
      )}
      {playbook && (
        <PlaybookReader
          key={`${clientSlug}:${playbook.id}`}
          id={playbook.id}
          initialTab={readerTab}
          clientSlug={clientSlug}
          workspaceName={workspaceName}
          agentName={agentName}
          entries={allEntries}
          onClose={() => setOpened(null)}
          onLocate={locate}
        />
      )}
      {catalogSource && (
        <Dialog.Root open onOpenChange={(open) => !open && setOpened(null)}>
          <Dialog.Portal>
            <Dialog.Overlay className="solar-dialog-overlay" />
            <Dialog.Content className="atlas-catalog-dialog">
              <span className="atlas-eyebrow">
                SMART MARKETER / SOURCE LIBRARY
              </span>
              <BookOpen className="atlas-catalog-icon" size={34} />
              <Dialog.Title>{catalogSource.title}</Dialog.Title>
              <Dialog.Description>{catalogSource.course}</Dialog.Description>
              <div className="atlas-catalog-facts">
                <span>
                  <strong>{catalogSource.passageCount}</strong>Indexed passages
                </span>
                <span>
                  <strong>v{catalogSource.packageVersion}</strong>Connected
                  package
                </span>
              </div>
              <p>
                This lesson is part of the knowledge your agent can retrieve.
                The catalog shows its source information; an in-depth editorial
                playbook is a separate publication.
              </p>
              <details>
                <summary>Source reference</summary>
                <code>{catalogSource.id}</code>
              </details>
              {catalogData?.playbooks
                ?.filter((p) => p.sourceIds.includes(catalogSource.id))
                .map((p) => (
                  <button
                    className="solar-primary"
                    key={p.id}
                    onClick={() => explorePlaybook(p)}
                  >
                    Read {p.title}
                    <ArrowRight size={15} />
                  </button>
                ))}
              <Dialog.Close
                className="atlas-reader-close"
                aria-label="Close source information"
              >
                <X size={20} />
              </Dialog.Close>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}
export default function Brain() {
  const { activeClient, member } = useApp();
  return (
    <BrainBody
      key={`${member?.id || member?.email || ""}:${activeClient?.clientSlug || "none"}`}
      clientSlug={activeClient?.clientSlug || ""}
    />
  );
}
